import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import { Address } from "@/services/backend/addresses/types/address";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { TextInput } from "@/shared/components/form/text-input";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import { googleMapsSearchUrl } from "@/services/backend/addresses/lib/maps-url";

type Mutation<Req, Res> = () => RtkQueryMutation<Req, Res>;

interface ServiceAddressCardProps {
  address: Address;
  onAddressChange: (serviceAddress: Address) => void;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
  hideNavigationButton?: boolean;
}

export function ServiceAddressCard({
  address,
  onAddressChange,
  title = t("Serviceadresse"),
  description = t("Adresse an der der Service durchgeführt werden soll."),
  fieldErrors = undefined,
  resetFieldError = undefined,
  disabled = false,
  hideNavigationButton = false,
}: ServiceAddressCardProps) {
  const errorMessages = (field: string): string[] => {
    if (!field || field === "") {
      return [];
    }

    return fieldErrors ? (fieldErrors[field] ?? []) : [];
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {!hideNavigationButton && (
          <a
            href={googleMapsSearchUrl(address)}
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="outline">{t("In Karte öffnen")}</Button>
          </a>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <TextareaInput
          label={t("Kommentar")}
          field="comment"
          text={address.comment}
          onTextChange={(comment) => {
            if (resetFieldError) resetFieldError("comment");
            onAddressChange({ ...address, comment });
          }}
          errors={errorMessages("comment")}
          disabled={disabled}
        />
        <TextInput
          label={t("Firma")}
          field="company"
          text={address.company}
          onTextChange={(company) => {
            if (resetFieldError) resetFieldError("company");
            onAddressChange({ ...address, company });
          }}
          errors={errorMessages("company")}
          disabled={disabled}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label={t("Straße & Hausnummer")}
            field="street"
            text={address.street}
            onTextChange={(street) => {
              if (resetFieldError) resetFieldError("street");
              onAddressChange({ ...address, street });
            }}
            errors={errorMessages("street")}
            disabled={disabled}
          />
          <div className="grid grid-cols-4 gap-4">
            <TextInput
              label={t("PLZ")}
              field="postalCode"
              text={address.postalCode}
              onTextChange={(postalCode) => {
                if (resetFieldError) resetFieldError("postalCode");
                onAddressChange({ ...address, postalCode });
              }}
              errors={errorMessages("postalCode")}
              disabled={disabled}
            />
            <TextInput
              label={t("Stadt")}
              field="city"
              text={address.city}
              onTextChange={(city) => {
                if (resetFieldError) resetFieldError("city");
                onAddressChange({ ...address, city });
              }}
              errors={errorMessages("city")}
              disabled={disabled}
              className="col-span-3"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ServiceAddressCardWithMutation<
  Req extends { id: string; serviceAddress: Address },
  Res,
>({
  id,
  address,
  mutation,
  title = t("Serviceadresse"),
  description = t("Adresse an der der Service durchgeführt werden soll."),
  fieldErrors = undefined,
  resetFieldError = undefined,
  disabled = false,
}: {
  id: string;
  address: Address;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
}) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, serviceAddress: address });
  const debouncedMutation = mutation();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, serviceAddress: address },
  );

  const joinedFieldErrors = {
    ...fieldErrors,
    ...(rtkErrIsValidationError(error) ? error.data.errors : {}),
  } as { [key: string]: string[] };

  return (
    <ServiceAddressCard
      address={request.serviceAddress}
      // @ts-expect-error - I do not understand the error. But it works.
      onAddressChange={(a) => setRequest({ id, serviceAddress: a })}
      title={title}
      description={description}
      fieldErrors={joinedFieldErrors}
      resetFieldError={resetFieldError}
      disabled={disabled}
    />
  );
}
